import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { InventoryAxios } from "api/InventoryAxios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export interface PaginatedResponse<T> {
    count: number;
    total_pages: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

export const fetchStockOverview = createAsyncThunk(
    "inventory/fetchStockOverview",
    async ({
        page,
        pageSize,
        sort,
        order,
        search,
        filters,
        warehouseId,
    }: {
        page: number;
        pageSize: number;
        search?: string;
        sort?: string;
        order?: "asc" | "desc" | "";
        filters?: any;
        warehouseId?: string;
    }) => {
        try {
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("page_size", pageSize.toString());
            params.append("warehouse_id", warehouseId?.toString() || "");

            if (search) params.append("search", search);
            if (sort) {
                const orderingValue = order === "desc" ? `-${sort}` : sort;
                params.append("ordering", orderingValue);
            }

            const filterPayload = FilterBuilder(filters ?? []);


            const body = {
                filters: filterPayload,
            };

            const response: any = await InventoryAxios.post(
                `${API_ENDPOINT.INVENTORY.IN_STOCK.STOCKOVERVIEW.LIST}?${params.toString()}`,
                body
            );
            return {
                data: response.data.data || [],
                rowCount: response.data.count || 0,
            };
        } catch (error: any) {
            console.error(
                "fetch Inventory List error:",
                error.response?.data || error.message
            );
            throw new Error("Network Error");
        }
    }
);

export const fetchReorderLevelDropDown = createAsyncThunk(
    "inventory/fetchReorderLevelDropDown",
    async (_, { rejectWithValue }) => {
        try {
            const response = await InventoryAxios.get(
                API_ENDPOINT.INVENTORY.REORDER_LEVEL.DROPDOWN
            );
            return response.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch reorder level dropdown data"
            );
        }
    }
);

export const fetchStockOverviewDetail = createAsyncThunk(
    "inventory/fetchStockOverviewDetail",
    async ({
        page,
        pageSize,
        sort,
        order,
        search,
        filters,
        warehouseId,
        id
    }: {
        page: number;
        pageSize: number;
        search?: string;
        sort?: string;
        order?: "asc" | "desc" | "";
        filters?: any;
        warehouseId?: string;
        id: string;
    }) => {
        try {
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("page_size", pageSize.toString());
            params.append("product_id", id);
            params.append("warehouse_id", warehouseId?.toString() || "");

            if (search) params.append("search", search);
            if (sort) {
                const orderingValue = order === "desc" ? `-${sort}` : sort;
                params.append("ordering", orderingValue);
            }

            const filterPayload = FilterBuilder(filters ?? []);


            const body = {
                filters: filterPayload,
            };

            const response: any = await InventoryAxios.post(
                `${API_ENDPOINT.INVENTORY.IN_STOCK.STOCKOVERVIEW.DETAIL}?${params.toString()}`,
                body
            );
            return {
                data: response.data.data || [],
                rowCount: response.data.count || 0,
            };
        } catch (error: any) {
            console.error(
                "fetch Inventory List error:",
                error.response?.data || error.message
            );
            throw new Error("Network Error");
        }
    }
);

export const exportInStockCSV = async ({
  warehouse_id,
  search,
  filters,
}: {
  warehouse_id: string;
  search?: string;
  filters?: any[];
}) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);

  const filterPayload = FilterBuilder(filters ?? []);
  const body = {
    filters: filterPayload,
  };

  const response = await InventoryAxios.post(
    `${API_ENDPOINT.INVENTORY.IN_STOCK.STOCKOVERVIEW.EXPORT(warehouse_id)}&${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `instock_Stockoverview${new Date().toISOString().split("T")[0]}.csv`,
  };
};

